/**
 * imageCompression.js
 * 
 * Utility per la compressione automatica delle immagini nel browser
 * Converte foto da 5MB a 300KB prima di caricarle su Firebase
 */

import imageCompression from 'browser-image-compression';

/**
 * Comprime un'immagine singola
 * @param {File} file - File immagine da comprimere
 * @param {Object} options - Opzioni personalizzate
 * @returns {Promise<File>} - File compresso
 */
export const compressImage = async (file, options = {}) => {
  try {
    // Defaults minimi e non opinionati: il chiamante (imageToStorage.js) passa
    // sempre maxSizeMB/maxWidthOrHeight/initialQuality espliciti — i default
    // qui servono solo come rete di sicurezza per chiamate dirette senza opzioni.
    // NON forziamo fileType: lasciamo che browser-image-compression scelga il
    // formato ottimale invece di imporre sempre WebP, che con compressione
    // aggressiva produce artefatti su foto di veicoli (cromature, riflessi).
    const defaultOptions = {
      maxSizeMB: 2,
      maxWidthOrHeight: 2000,
      useWebWorker: true,
      initialQuality: 0.92,
    };

    const finalOptions = { ...defaultOptions, ...options };

    console.log(`Compressione: ${(file.size / 1024 / 1024).toFixed(2)} MB → ${finalOptions.maxSizeMB} MB`);

    const compressedFile = await imageCompression(file, finalOptions);

    console.log(`Risultato: ${(compressedFile.size / 1024).toFixed(2)} KB`);

    return compressedFile;
  } catch (error) {
    console.error('Errore compressione:', error);
    throw error;
  }
};

/**
 * Comprime più immagini in sequenza
 * @param {File[]} files - Array di file da comprimere
 * @param {Object} options - Opzioni personalizzate
 * @returns {Promise<File[]>} - Array di file compressi
 */
export const compressMultipleImages = async (files, options = {}) => {
  const compressedFiles = [];

  for (let i = 0; i < files.length; i++) {
    try {
      const file = files[i];
      const compressed = await compressImage(file, options);
      compressedFiles.push(compressed);
      console.log(`${i + 1}/${files.length} immagini compresse`);
    } catch (error) {
      console.error(`Errore compressione immagine ${i + 1}:`, error);
      // Continua con le altre immagini anche se una fallisce
    }
  }

  return compressedFiles;
};

/**
 * Calcola la percentuale di riduzione
 */
export const calculateCompressionPercentage = (originalSize, compressedSize) => {
  return Math.round(((originalSize - compressedSize) / originalSize) * 100);
};
